import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import GoogleAccountButton from '@/components/GoogleAccountButton';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Sparkles } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { persistCsrfToken } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { countryOptions } from '@/lib/quoteCountries';
import { trackSignUp } from '@/lib/analytics';

/**
 * QuoteRegister
 * -------------
 * Standalone self-service registration for Quote Builder accounts.
 * Reuses `/api/quote/auth/register` (bcrypt / strict email / rate-limit).
 * On success the backend already issued auth cookies, so we:
 *   1. Persist the returned access token in sessionStorage.
 *   2. Refresh AuthContext (`checkAuth`) to populate `user`.
 *   3. Navigate to `/quote-setup`.
 */
export const QuoteRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [googleCredential, setGoogleCredential] = useState(location.state?.googleCredential || '');
  const { checkAuth, logout } = useAuth();
  const { t, i18n } = useTranslation('quotes');
  const API_URL = process.env.REACT_APP_API_URL;

  const [name, setName] = useState(location.state?.googleProfile?.name || '');
  const [email, setEmail] = useState(location.state?.googleProfile?.email || '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [invoiceTitle, setInvoiceTitle] = useState('');
  const [taxOffice, setTaxOffice] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogle = async (credential) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/auth/google`, { id_token: credential }, { withCredentials: true });
      sessionStorage.setItem('access_token', data.access_token);
      persistCsrfToken(data.csrf_token);
      await checkAuth();
      const result = await axios.get(`${API_URL}/api/quote/setup-status`);
      if (result.data.eligible === false) {
        await logout();
        toast.error(t('register.error'));
        return;
      }
      navigate(result.data.setup_completed ? '/quotes' : '/quote-setup', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'registration_required') {
        setGoogleCredential(credential);
        setEmail(detail.google_profile.email);
        setName(detail.google_profile.name || '');
      } else toast.error(typeof detail === 'string' ? detail : detail?.message || t('register.error'));
    } finally { setLoading(false); }
  };

  const selectCountry = (code) => {
    setCountryCode(code);
    if (code) {
      const currentLang = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
      const supported = ['tr', 'en', 'de', 'fr', 'es'];
      const nextLang = code === 'TR'
        ? 'tr'
        : (supported.includes(currentLang) && currentLang !== 'tr' ? currentLang : 'en');
      i18n.changeLanguage(nextLang);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error(t('register.validationTerms'));
      return;
    }
    if (!invoiceTitle.trim()) {
      toast.error(t('register.validationInvoiceTitle'));
      return;
    }
    if (countryCode === 'TR' && (!taxOffice.trim() || !/^\d{10,11}$/.test(taxNumber.trim()))) {
      toast.error(t('register.validationTurkeyTax'));
      return;
    }
    if (!googleCredential && password !== passwordConfirm) {
      toast.error(t('register.validationMismatch'));
      return;
    }
    if (!googleCredential && password.length < 8) {
      toast.error(t('register.validationLength'));
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quote/auth/register`,
        {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          ...(googleCredential ? { google_id_token: googleCredential } : { password, password_confirm: passwordConfirm }),
          country_code: countryCode,
          invoice_title: invoiceTitle.trim(),
          tax_office: countryCode === 'TR' ? taxOffice.trim() : null,
          tax_number: countryCode === 'TR' ? taxNumber.trim() : null,
          terms_accepted: termsAccepted,
        },
        { withCredentials: true },
      );
      if (data.access_token) {
        try { sessionStorage.setItem('access_token', data.access_token); } catch { /* noop */ }
      }
      if (data.csrf_token) {
        persistCsrfToken(data.csrf_token);
      }
      trackSignUp({
        method: googleCredential ? 'google' : 'email',
        country: countryCode,
      });
      toast.success(t('register.success'));
      await checkAuth();
      navigate('/quote-setup', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('register.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4"
      data-testid="quote-register-page"
    >
      <div className="w-full max-w-sm space-y-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-3">
            <img
              src="/logo-paydaai-icon.png"
              alt="PaydaAI"
              className="h-14 w-14 sm:h-16 sm:w-16 object-contain drop-shadow-md rounded-2xl"
              data-testid="quote-register-logo"
            />
            <span className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A2E]">
              PaydaAI
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-[#2563EB] flex items-center justify-center gap-1.5" data-testid="quote-register-title">
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              {t('register.title')}
            </p>
            <p className="text-xs text-[#595959] mt-1">
              {t('register.subtitle')}
            </p>
          </div>
        </div>

        <Card className="border border-[#E5E5E5] shadow-lg" data-testid="quote-register-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to="/"
                className="p-1.5 rounded-lg hover:bg-[#F0F0F0] transition"
                data-testid="quote-register-back"
              >
                <ArrowLeft className="w-5 h-5 text-[#595959]" />
              </Link>
              <p className="text-sm font-semibold text-[#1A1A2E]">{t('register.cardTitle')}</p>
            </div>

            <GoogleAccountButton onCredential={handleGoogle} disabled={loading} />
            {googleCredential && (
              <p className="text-sm text-blue-700 mb-4">
                {t('register.googleVerified')}
              </p>
            )}
            <form onSubmit={submit} className="space-y-4" data-testid="quote-register-form">
              <div className="space-y-1.5">
                <Label className="text-sm">{t('register.fullName')}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('register.fullNamePlaceholder')}
                  required
                  minLength={2}
                  maxLength={100}
                  data-testid="quote-register-name-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('register.email')}</Label>
                <Input
                  type="email"
                  readOnly={Boolean(googleCredential)}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('register.emailPlaceholder')}
                  required
                  data-testid="quote-register-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('register.country')}</Label>
                <select
                  value={countryCode}
                  onChange={(e) => selectCountry(e.target.value)}
                  required
                  data-testid="quote-register-country-select"
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('register.countryPlaceholder')}</option>
                  {countryOptions((i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]).map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('register.invoiceTitle')}</Label>
                <Input
                  value={invoiceTitle}
                  onChange={(e) => setInvoiceTitle(e.target.value)}
                  placeholder={t('register.invoiceTitlePlaceholder')}
                  required
                  minLength={2}
                  maxLength={200}
                  data-testid="quote-register-invoice-title-input"
                  className="h-11"
                />
              </div>
              {countryCode === 'TR' && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('register.taxOffice')}</Label>
                    <Input
                      value={taxOffice}
                      onChange={(e) => setTaxOffice(e.target.value)}
                      placeholder={t('register.taxOfficePlaceholder')}
                      required
                      maxLength={100}
                      data-testid="quote-register-tax-office-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('register.taxNumber')}</Label>
                    <Input
                      inputMode="numeric"
                      value={taxNumber}
                      onChange={(e) => setTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      placeholder={t('register.taxNumberPlaceholder')}
                      required
                      minLength={10}
                      maxLength={11}
                      data-testid="quote-register-tax-number-input"
                      className="h-11"
                    />
                    <p className="text-[11px] text-[#94A3B8]">{t('register.taxNumberHint')}</p>
                  </div>
                </>
              )}
              {countryCode && countryCode !== 'TR' && (
                <p className="text-[11px] text-[#64748B] -mt-2">{t('register.foreignTaxHint')}</p>
              )}
              {!googleCredential && <><div className="space-y-1.5">
                <Label className="text-sm">{t('register.password')}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  required
                  minLength={8}
                  data-testid="quote-register-password-input"
                  className="h-11"
                />
                <p className="text-[11px] text-[#94A3B8]">
                  {t('register.passwordHint')}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('register.passwordConfirm')}</Label>
                <Input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder={t('register.passwordConfirmPlaceholder')}
                  required
                  minLength={8}
                  data-testid="quote-register-password-confirm-input"
                  className="h-11"
                />
              </div>

              </>}
              <div className="flex items-start gap-2.5 py-1">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(v === true)}
                  data-testid="quote-register-terms-checkbox"
                  className="mt-0.5"
                />
                <Label htmlFor="terms" className="text-xs leading-relaxed text-[#475569] cursor-pointer">
                  {t('register.termsPrefix')}
                  <a
                    href={(i18n.resolvedLanguage || i18n.language || 'tr').startsWith('tr') ? 'https://paydaai.com/kullanim-sartlari' : 'https://paydaai.com/en/terms-of-use'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] hover:underline"
                  >
                    {t('register.termsLink')}
                  </a>
                  {t('register.termsSuffix')}
                </Label>
              </div>
              {/* KVKK md.10: aydınlatma bir onay değil, bilgilendirmedir — kutunun dışında. */}
              <p className="text-xs leading-relaxed text-[#475569]" data-testid="quote-register-privacy-notice">
                {t('register.privacyNoticePrefix')}
                <a
                  href={(i18n.resolvedLanguage || i18n.language || 'tr').startsWith('tr') ? 'https://paydaai.com/kvkk-aydinlatma-metni' : 'https://paydaai.com/en/privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2563EB] hover:underline"
                >
                  {t('register.privacyNoticeLink')}
                </a>
                {t('register.privacyNoticeSuffix')}
              </p>

              <Button
                type="submit"
                className="w-full h-11 text-white gap-2 bg-[#2563EB] hover:bg-[#1E4ED8]"
                disabled={loading}
                data-testid="quote-register-submit"
              >
                {loading ? t('register.submitting') : (<><UserPlus className="w-4 h-4" /> {t('register.submit')}</>)}
              </Button>

              <p className="text-center text-xs text-[#595959] pt-2 border-t border-[#F1F5F9]">
                {t('register.haveAccount')}{' '}
                <Link to="/" className="text-[#2563EB] hover:underline font-medium" data-testid="quote-register-login-link">
                  {t('register.loginLink')}
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuoteRegister;
